// File: frontend/src/components/Users/UsersForm.jsx
// Create and edit user form component

import React, { useState, useEffect } from 'react';
import { useMutation } from 'react-query';
import { createUser, updateUser } from '../../services/api';
import { 
  UserIcon, 
  EnvelopeIcon, 
  ShieldCheckIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon 
} from '@heroicons/react/24/outline';

const UsersForm = ({ mode, user, roles, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: '',
    role_id: '',
    is_active: true
  });
  
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Initialize form data for edit mode
  useEffect(() => {
    if (mode === 'edit' && user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        password: '',
        confirm_password: '',
        role_id: user.role_id || '',
        is_active: user.is_active ?? true
      });
    }
  }, [mode, user]);

  // Create user mutation
  const createMutation = useMutation(createUser, {
    onSuccess: (response) => {
      onSuccess('Usuário criado com sucesso!');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || error.message;
      setErrors({ submit: errorMessage });
    }
  });

  // Update user mutation
  const updateMutation = useMutation(
    ({ id, data }) => updateUser(id, data),
    {
      onSuccess: (response) => {
        onSuccess('Usuário atualizado com sucesso!');
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || error.message;
        setErrors({ submit: errorMessage });
      }
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    // Basic validation
    const newErrors = {};
    
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'Nome é obrigatório';
    }
    
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Sobrenome é obrigatório';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    
    if (!formData.role_id) {
      newErrors.role_id = 'Perfil de acesso é obrigatório';
    }
    
    // Password validation for create mode or when password is provided
    if (mode === 'create' || formData.password) {
      if (!formData.password) {
        newErrors.password = 'Senha é obrigatória';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Senha deve ter pelo menos 6 caracteres';
      }
      
      if (formData.password !== formData.confirm_password) {
        newErrors.confirm_password = 'Senhas não conferem';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Prepare data for submission
    const submitData = {
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      email: formData.email.trim().toLowerCase(),
      role_id: parseInt(formData.role_id),
      is_active: formData.is_active
    };

    // Only include password if provided
    if (formData.password) {
      submitData.password = formData.password;
    }

    // Submit
    if (mode === 'create') {
      createMutation.mutate(submitData);
    } else {
      updateMutation.mutate({ id: user.id, data: submitData });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const selectedRole = roles.find(role => role.id === parseInt(formData.role_id));

  return (
    <div className="max-w-2xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Form Header */}
        <div className="text-center">
          <div className="mx-auto h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
            <UserIcon className="h-6 w-6 text-blue-600" />
          </div>
          <h2 className="mt-4 text-2xl font-bold text-gray-900">
            {mode === 'create' ? 'Criar Novo Usuário' : 'Editar Usuário'}
          </h2>
          <p className="mt-2 text-gray-600">
            {mode === 'create' 
              ? 'Preencha as informações para criar um novo usuário no sistema'
              : 'Edite as informações do usuário selecionado'
            }
          </p>
        </div>

        {/* Submit Error */}
        {errors.submit && (
          <div className="rounded-md bg-red-50 border border-red-200 p-4">
            <div className="text-sm text-red-600">{errors.submit}</div>
          </div>
        )}

        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 space-y-6">
          {/* Personal Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Informações Pessoais
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* First Name */}
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Nome *
                </label>
                <input
                  id="first_name"
                  name="first_name"
                  type="text"
                  value={formData.first_name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                    errors.first_name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Digite o nome"
                />
                {errors.first_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Sobrenome *
                </label>
                <input
                  id="last_name"
                  name="last_name"
                  type="text"
                  value={formData.last_name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                    errors.last_name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Digite o sobrenome"
                />
                {errors.last_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>
                )}
              </div>
            </div>
          </div>

          {/* Account Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Informações da Conta
            </h3>
            <div className="space-y-6">
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <div className="relative">
                  <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                      errors.email ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Digite o email"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              {/* Password Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Password */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    {mode === 'create' ? 'Senha *' : 'Nova Senha'}
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                        errors.password ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder={mode === 'create' ? 'Digite a senha' : 'Deixe em branco para manter atual'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 mb-1">
                    {mode === 'create' ? 'Confirmar Senha *' : 'Confirmar Nova Senha'}
                  </label>
                  <div className="relative">
                    <input
                      id="confirm_password"
                      name="confirm_password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirm_password}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                        errors.confirm_password ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Confirme a senha"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                    </button>
                  </div>
                  {errors.confirm_password && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirm_password}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Role and Permissions */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Perfil de Acesso e Permissões
            </h3>
            <div className="space-y-4">
              {/* Role Selection */}
              <div>
                <label htmlFor="role_id" className="block text-sm font-medium text-gray-700 mb-1">
                  Perfil de Acesso *
                </label>
                <div className="relative">
                  <ShieldCheckIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <select
                    id="role_id"
                    name="role_id"
                    value={formData.role_id}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                      errors.role_id ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Selecione um perfil de acesso</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name} - {role.description}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.role_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.role_id}</p>
                )}
              </div>

              {/* Role Description */}
              {selectedRole && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">
                    Permissões do perfil "{selectedRole.name}":
                  </h4>
                  <div className="text-sm text-blue-800">
                    <p className="mb-2">{selectedRole.description}</p>
                    <p className="text-xs">
                      Escopo: {selectedRole.permissions?.scope === 'global' ? 'Global (todos os dados)' : 
                              selectedRole.permissions?.scope === 'team' ? 'Equipe' : 'Próprios dados apenas'}
                    </p>
                  </div>
                </div>
              )}

              {/* Active Status */}
              <div className="flex items-center">
                <input
                  id="is_active"
                  name="is_active"
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 flex items-center text-sm text-gray-700">
                  <CheckCircleIcon className="h-4 w-4 mr-1 text-green-500" />
                  Usuário ativo (pode acessar o sistema)
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end space-x-4 pt-6">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={createMutation.isLoading || updateMutation.isLoading}
            className="px-6 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {createMutation.isLoading || updateMutation.isLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {mode === 'create' ? 'Criando...' : 'Salvando...'}
              </div>
            ) : (
              mode === 'create' ? 'Criar Usuário' : 'Salvar Alterações'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UsersForm;